/**
 * Data layer for the `/show/:id` route (web/docs/data-layer.md). Mirrors the
 * mobile real client for the detail surface (`app/src/data/api-real.ts`):
 * reads degrade on `404`, and every mutation is optimistic — patch in
 * `onMutate`, roll back in `onError`, invalidate in `onSettled`, with a toast
 * + Retry on failure (the web app has no persisted mutation queue — ui-ux.md
 * §7). Progress-driven status transitions use the shared
 * `nextStatusOnEpisodeToggle` so an episode/season mark flips watching →
 * completed → watching exactly like the app (docs/api-schema.md §Status
 * behavior).
 */

import { useCallback, useEffect, useRef } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { apiDelete, apiGet, apiPost, apiPut, ApiError } from '@/api/http';
import { useRatingPrompt } from '@/components/RatingPrompt';
import { translate } from '@/i18n/locale';

import type {
  ApiRatingResponse,
  ApiRewatchResponse,
  ApiSeasonDetail,
  ApiSeasonsResponse,
  ApiSeasonUnwatchResponse,
  ApiSeasonWatchResponse,
  ApiShow,
  ApiShowDetail,
  ApiShowsPage,
  ApiStatusResponse,
  ApiTitleRatingResponse,
  ApiWatchResponse,
} from '@shared/api-types';
import {
  mapEpisode,
  mapSeasonSummary,
  mapShowDetailToTitle,
  mapShowToTitle,
} from '@shared/mappers';
import { resumeEpisode, resumeFromSummaries, resumeSeason } from '@shared/resume';
import { nextStatusOnEpisodeToggle } from '@shared/status-transitions';
import type { Episode, Season, Title, TitleType, UserShowStatus } from '@shared/types';

/**
 * TMDB show and movie ids overlap (121 is Doctor Who and The Two Towers), so a
 * movie's cache entry carries its type; shows keep the historical key.
 */
export const showKey = (id: string, type?: TitleType | null) =>
  type === 'movie' ? (['show', id, 'movie'] as const) : (['show', id] as const);

/** `?type=` for title routes — picks the right catalog row (docs/api-schema.md §Shows). */
const typeQuery = (type?: TitleType | null) => (type ? { query: { type } } : undefined);
export const seasonsKey = (id: string) => ['seasons', id] as const;
export const seasonKey = (id: string, seasonNumber: number) =>
  ['season', id, seasonNumber] as const;

async function or404Null<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** `GET /v1/shows/:id` → domain `Title`, or null when the id is unknown. */
export async function fetchShow(id: string, type?: TitleType | null): Promise<Title | null> {
  const res = await or404Null(() => apiGet<ApiShowDetail>(`/v1/shows/${id}`, typeQuery(type)));
  return res ? mapShowDetailToTitle(res) : null;
}

/** `GET /v1/shows/:id/seasons` → season summaries (episodes load lazily). */
export async function fetchSeasons(id: string): Promise<Season[]> {
  const res = await or404Null(() => apiGet<ApiSeasonsResponse>(`/v1/shows/${id}/seasons`));
  return (res?.seasons ?? [])
    .filter((s): s is NonNullable<typeof s> => Boolean(s))
    .map(mapSeasonSummary);
}

/** `GET /v1/shows/:id/seasons/:n` → one season with its episodes. */
export async function fetchSeason(id: string, seasonNumber: number): Promise<Season | null> {
  const res = await or404Null(
    () => apiGet<ApiSeasonDetail>(`/v1/shows/${id}/seasons/${seasonNumber}`),
  );
  if (!res) return null;
  return {
    seasonNumber: Math.round(res.season.season_number),
    episodeCount: Math.round(res.season.episode_count),
    name: res.season.name ?? undefined,
    nameBr: res.season.name_br ?? undefined,
    episodes: (res.episodes ?? [])
      .filter((e): e is NonNullable<typeof e> => Boolean(e))
      .map(mapEpisode),
  };
}

/**
 * The title. Data comes from the query cache — PageTransition re-serves
 * cached route elements, so the screen must not rely on a fresh mount
 * (web/docs/data-layer.md).
 */
export function useShow(id: string, type?: TitleType | null) {
  return useQuery({ queryKey: showKey(id, type), queryFn: () => fetchShow(id, type) });
}

/** Hover intent before a cover counts as "about to open" (not just passing over). */
const PREFETCH_INTENT_MS = 150;

/**
 * Warm a title page before it opens: the detail and its seasons, keyed
 * exactly as the route reads them (a show's path carries no `?type`). A first
 * open then renders like a cached one — no skeleton, so the cover flight
 * still runs (lib/flight.ts). Within the cache's `staleTime` it's a no-op.
 */
export function useTitlePrefetch(id: string, type?: TitleType | null) {
  const qc = useQueryClient();
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const routeType = type === 'movie' ? 'movie' : undefined;
  const prefetch = () => {
    void qc.prefetchQuery({ queryKey: showKey(id, routeType), queryFn: () => fetchShow(id, routeType) });
    if (routeType !== 'movie') void qc.prefetchQuery({ queryKey: seasonsKey(id), queryFn: () => fetchSeasons(id) });
  };
  const cancel = () => clearTimeout(timer.current);
  useEffect(() => cancel, []);
  return {
    onPointerEnter: () => {
      cancel();
      timer.current = setTimeout(prefetch, PREFETCH_INTENT_MS);
    },
    onPointerLeave: cancel,
    onFocus: prefetch,
  };
}

export function useSeasons(id: string, enabled = true) {
  return useQuery({ queryKey: seasonsKey(id), queryFn: () => fetchSeasons(id), enabled });
}

export function useSeason(id: string, seasonNumber: number, enabled = true) {
  return useQuery({
    queryKey: seasonKey(id, seasonNumber),
    queryFn: () => fetchSeason(id, seasonNumber),
    enabled,
  });
}

// --- similar titles -------------------------------------------------------------

export const similarKey = (id: string, type?: TitleType | null) =>
  type === 'movie' ? (['similar', id, 'movie'] as const) : (['similar', id] as const);

/** Catalog reads change on the 12h refresh cycle — 5min stale is plenty. */
const CATALOG_STALE_MS = 5 * 60_000;

/**
 * `GET /v1/shows/:id/similar` → ranked "more like this" titles (full catalog
 * rows, same shape as `/v1/shows/genre`). Degrades to empty on `404` (unknown
 * title) and `503` (search database unavailable) — docs/api-schema.md §Shows.
 */
export async function fetchSimilar(id: string, type?: TitleType | null): Promise<Title[]> {
  try {
    const res = await apiGet<ApiShowsPage>(`/v1/shows/${id}/similar`, {
      query: { limit: 20, type: type ?? undefined },
    });
    return (res.shows ?? []).filter((s): s is ApiShow => Boolean(s)).map(mapShowToTitle);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 503)) return [];
    throw err;
  }
}

/**
 * The Similar section's data. `mediaTypeReady` gates the fetch until the title
 * detail has resolved (the section renders from the cached `['show', id]` query,
 * so the gate never refetches).
 */
export function useSimilar(id: string, mediaTypeReady: boolean, type?: TitleType | null) {
  return useQuery({
    queryKey: similarKey(id, type),
    queryFn: () => fetchSimilar(id, type),
    enabled: mediaTypeReady,
    staleTime: CATALOG_STALE_MS,
  });
}

// --- optimistic cache plumbing ---------------------------------------------

interface ShowScopeSnapshot {
  show?: Title | null;
  seasons?: Season[];
  /** Episode lists, keyed by season number. */
  seasonsDetail: Map<number, Season | null>;
}

function clampWatched(watched: number, episodeCount: number): number {
  return Math.max(0, Math.min(episodeCount, watched));
}

function withProgress(title: Title, watched: number): Title {
  const total = title.totalEpisodes ?? 0;
  const clamped = total > 0 ? clampWatched(watched, total) : Math.max(0, watched);
  return {
    ...title,
    watchedEpisodes: clamped,
    progressPercent:
      total > 0 ? Math.round((clamped / total) * 1000) / 10 : title.progressPercent,
  };
}

function patchShow(
  qc: QueryClient,
  id: string,
  patch: (title: Title) => Title,
  type?: TitleType | null,
) {
  qc.setQueryData<Title | null>(showKey(id, type), (old) => (old ? patch(old) : old));
}

function patchSeasonSummaries(qc: QueryClient, id: string, patch: (season: Season) => Season) {
  qc.setQueryData<Season[] | undefined>(seasonsKey(id), (old) => old?.map(patch));
}

function patchSeasonDetail(
  qc: QueryClient,
  id: string,
  seasonNumber: number,
  patchEpisode: (episode: Episode) => Episode,
) {
  qc.setQueryData<Season | null | undefined>(seasonKey(id, seasonNumber), (old) =>
    old ? { ...old, episodes: old.episodes.map(patchEpisode) } : old,
  );
}

/** Every cached episode list for a title (rewatch/remove reset all of them). */
function patchAllSeasonDetails(
  qc: QueryClient,
  id: string,
  patchEpisode: (episode: Episode) => Episode,
) {
  for (const [key] of qc.getQueriesData<Season | null>({ queryKey: ['season', id] })) {
    qc.setQueryData<Season | null>(key, (old) =>
      old ? { ...old, episodes: old.episodes.map(patchEpisode) } : old,
    );
  }
}

function snapshotShowScope(qc: QueryClient, id: string, type?: TitleType | null): ShowScopeSnapshot {
  const seasonsDetail = new Map<number, Season | null>();
  for (const [key, data] of qc.getQueriesData<Season | null>({ queryKey: ['season', id] })) {
    const seasonNumber = key[2];
    if (typeof seasonNumber === 'number') seasonsDetail.set(seasonNumber, data ?? null);
  }
  return {
    show: qc.getQueryData<Title | null>(showKey(id, type)),
    seasons: qc.getQueryData<Season[]>(seasonsKey(id)),
    seasonsDetail,
  };
}

function restoreShowScope(
  qc: QueryClient,
  id: string,
  snapshot: ShowScopeSnapshot | undefined,
  type?: TitleType | null,
) {
  if (!snapshot) return;
  if (snapshot.show !== undefined) qc.setQueryData(showKey(id, type), snapshot.show);
  if (snapshot.seasons !== undefined) qc.setQueryData(seasonsKey(id), snapshot.seasons);
  for (const [seasonNumber, data] of snapshot.seasonsDetail) {
    qc.setQueryData(seasonKey(id, seasonNumber), data);
  }
}

/** Detail + seasons + every cached episode list + the library shelf. */
function invalidateShowScope(qc: QueryClient, id: string) {
  void qc.invalidateQueries({ queryKey: showKey(id) });
  void qc.invalidateQueries({ queryKey: seasonsKey(id) });
  void qc.invalidateQueries({ queryKey: ['season', id] });
  void qc.invalidateQueries({ queryKey: ['home'] });
}

/**
 * Mutations with a toast + Retry on failure. The ref makes the Retry action
 * re-enter the same mutation (fresh onMutate/onError cycle) without closing
 * over a stale `mutate`. Toasts translate through the module-level locale
 * mirror (translate()) so they read correctly even outside React render.
 */
function useToastedMutation<TData, TVariables, TOnMutateResult>(
  options: UseMutationOptions<TData, Error, TVariables, TOnMutateResult>,
  errorMessage: (vars: TVariables) => string,
) {
  const mutateRef = useRef<(vars: TVariables) => void>(() => {});
  const { onError, ...rest } = options;
  const mutation = useMutation<TData, Error, TVariables, TOnMutateResult>({
    ...rest,
    onError: (error, vars, onMutateResult, context) => {
      onError?.(error, vars, onMutateResult, context);
      toast.error(errorMessage(vars), {
        action: { label: translate('common.retry'), onClick: () => mutateRef.current(vars) },
      });
    },
  });
  mutateRef.current = mutation.mutate;
  return mutation;
}

/**
 * After a TITLE-level completion lands — a film marked watched, a series set
 * to completed, or finished by its last episode/season — ask for a rating
 * when the viewer has none (`RatingPrompt`, ui-ux.md §7). Living in the
 * shared mutation hooks, every surface (title page, Tonight's Continue, Up
 * next, Library quick actions) gets it with no per-screen code. Outside a
 * `RatingPromptProvider` it does nothing — no extra fetch.
 */
function usePromptRatingOnCompletion(id: string, type?: TitleType | null) {
  const qc = useQueryClient();
  const ratingPrompt = useRatingPrompt();
  return useCallback(async () => {
    if (!ratingPrompt) return;
    let title = qc.getQueryData<Title | null>(showKey(id, type));
    if (title === undefined) {
      try {
        title = await qc.fetchQuery({ queryKey: showKey(id, type), queryFn: () => fetchShow(id, type) });
      } catch {
        return;
      }
    }
    if (title && !title.userRating) ratingPrompt.prompt(title);
  }, [qc, id, type, ratingPrompt]);
}

// --- mutations ----------------------------------------------------------------

/** `PUT /v1/shows/:id/status` — the status menu + removal. */
export function useSetStatus(id: string, type?: TitleType | null) {
  const qc = useQueryClient();
  const promptRating = usePromptRatingOnCompletion(id, type);
  return useToastedMutation(
    {
      mutationFn: (status: UserShowStatus) =>
        apiPut<ApiStatusResponse>(`/v1/shows/${id}/status`, { status }, typeQuery(type)),
      onMutate: async (status) => {
        await qc.cancelQueries({ queryKey: showKey(id, type) });
        const previous = snapshotShowScope(qc, id, type);
        patchShow(
          qc,
          id,
          (title) =>
            status === 'removed'
              ? withProgress({ ...title, userStatus: status }, 0)
              : { ...title, userStatus: status },
          type,
        );
        if (status === 'removed') {
          // Removal deletes the watched-episode records server-side — mirror it.
          patchSeasonSummaries(qc, id, (s) => ({ ...s, watchedEpisodes: 0 }));
          patchAllSeasonDetails(qc, id, (e) => ({ ...e, watched: false }));
        }
        return { previous };
      },
      onError: (_err, _status, ctx) => {
        restoreShowScope(qc, id, ctx?.previous, type);
      },
      onSuccess: (_data, status) => {
        if (status === 'completed') void promptRating();
      },
      onSettled: () => invalidateShowScope(qc, id),
    },
    () => translate('show.error.updateStatus'),
  );
}

export interface EpisodeToggleVars {
  season: number;
  episode: number;
  watched: boolean;
  /** Status the progress transition needs, or null when it stays the same. */
  statusAfter: UserShowStatus | null;
}

/** POST/DELETE one episode watch mark (+ the status flip when one happens). */
export function useEpisodeToggle(id: string) {
  const qc = useQueryClient();
  const promptRating = usePromptRatingOnCompletion(id);
  return useToastedMutation(
    {
      mutationFn: async (vars: EpisodeToggleVars) => {
        const path = `/v1/shows/${id}/seasons/${vars.season}/episodes/${vars.episode}/watch`;
        const watchOp = vars.watched
          ? apiPost<ApiWatchResponse>(path)
          : apiDelete<ApiWatchResponse>(path);
        const statusOp = vars.statusAfter
          ? apiPut<ApiStatusResponse>(`/v1/shows/${id}/status`, { status: vars.statusAfter }, typeQuery('show'))
          : Promise.resolve(null);
        await Promise.all([watchOp, statusOp]);
      },
      onMutate: async (vars) => {
        await Promise.all([
          qc.cancelQueries({ queryKey: showKey(id) }),
          qc.cancelQueries({ queryKey: seasonsKey(id) }),
          qc.cancelQueries({ queryKey: seasonKey(id, vars.season) }),
        ]);
        const previous = snapshotShowScope(qc, id);
        const delta = vars.watched ? 1 : -1;
        patchShow(qc, id, (title) => {
          const next = withProgress(title, (title.watchedEpisodes ?? 0) + delta);
          return vars.statusAfter ? { ...next, userStatus: vars.statusAfter } : next;
        });
        patchSeasonSummaries(qc, id, (s) =>
          s.seasonNumber === vars.season
            ? {
                ...s,
                watchedEpisodes: clampWatched((s.watchedEpisodes ?? 0) + delta, s.episodeCount),
              }
            : s,
        );
        patchSeasonDetail(qc, id, vars.season, (e) =>
          e.episodeNumber === vars.episode ? { ...e, watched: vars.watched } : e,
        );
        return { previous };
      },
      onError: (_err, _vars, ctx) => {
        restoreShowScope(qc, id, ctx?.previous);
      },
      // The last remaining episode/season finishes the series — a title-level
      // completion; a plain episode mark never prompts.
      onSuccess: (_data, vars) => {
        if (vars.statusAfter === 'completed') void promptRating();
      },
      onSettled: () => invalidateShowScope(qc, id),
    },
    (vars) =>
      vars.watched
        ? translate('show.error.markEpisodeWatched')
        : translate('show.error.unmarkEpisode'),
  );
}

export interface SeasonToggleVars {
  season: number;
  watched: boolean;
  /** Status the progress transition needs, or null when it stays the same. */
  statusAfter: UserShowStatus | null;
}

/** POST/DELETE a whole season's watch marks (+ the status flip). */
export function useSeasonToggle(id: string) {
  const qc = useQueryClient();
  const promptRating = usePromptRatingOnCompletion(id);
  return useToastedMutation(
    {
      mutationFn: async (vars: SeasonToggleVars) => {
        const path = `/v1/shows/${id}/seasons/${vars.season}/watch`;
        const watchOp = vars.watched
          ? apiPost<ApiSeasonWatchResponse>(path)
          : apiDelete<ApiSeasonUnwatchResponse>(path);
        const statusOp = vars.statusAfter
          ? apiPut<ApiStatusResponse>(`/v1/shows/${id}/status`, { status: vars.statusAfter }, typeQuery('show'))
          : Promise.resolve(null);
        await Promise.all([watchOp, statusOp]);
      },
      onMutate: async (vars) => {
        await Promise.all([
          qc.cancelQueries({ queryKey: showKey(id) }),
          qc.cancelQueries({ queryKey: seasonsKey(id) }),
          qc.cancelQueries({ queryKey: seasonKey(id, vars.season) }),
        ]);
        const previous = snapshotShowScope(qc, id);
        const summary = qc
          .getQueryData<Season[]>(seasonsKey(id))
          ?.find((s) => s.seasonNumber === vars.season);
        const watchedInSeason = summary?.watchedEpisodes ?? 0;
        const delta = vars.watched
          ? (summary?.episodeCount ?? 0) - watchedInSeason
          : -watchedInSeason;
        patchShow(qc, id, (title) => {
          const next = withProgress(title, (title.watchedEpisodes ?? 0) + delta);
          return vars.statusAfter ? { ...next, userStatus: vars.statusAfter } : next;
        });
        patchSeasonSummaries(qc, id, (s) =>
          s.seasonNumber === vars.season
            ? { ...s, watchedEpisodes: vars.watched ? s.episodeCount : 0 }
            : s,
        );
        patchSeasonDetail(qc, id, vars.season, (e) => ({ ...e, watched: vars.watched }));
        return { previous };
      },
      onError: (_err, _vars, ctx) => {
        restoreShowScope(qc, id, ctx?.previous);
      },
      // The last remaining episode/season finishes the series — a title-level
      // completion; a plain episode mark never prompts.
      onSuccess: (_data, vars) => {
        if (vars.statusAfter === 'completed') void promptRating();
      },
      onSettled: () => invalidateShowScope(qc, id),
    },
    (vars) =>
      vars.watched ? translate('show.error.markSeasonWatched') : translate('show.error.unmarkSeason'),
  );
}

/**
 * "Finished" for a series: marks every unwatched season watched AND flips the
 * status to completed (the app's onMarkFullyWatched) so progress reads 100%.
 * For a movie use `useSetStatus('completed')` — there are no episodes.
 */
export function useFinishTitle(id: string, type?: TitleType | null) {
  const qc = useQueryClient();
  const promptRating = usePromptRatingOnCompletion(id, type);
  return useToastedMutation(
    {
      mutationFn: async () => {
        const seasons = await qc.fetchQuery({
          queryKey: seasonsKey(id),
          queryFn: () => fetchSeasons(id),
          staleTime: 30_000,
        });
        const seasonOps = (seasons ?? [])
          .filter((s) => s.episodeCount > 0 && (s.watchedEpisodes ?? 0) < s.episodeCount)
          .map((s) =>
            apiPost<ApiSeasonWatchResponse>(`/v1/shows/${id}/seasons/${s.seasonNumber}/watch`),
          );
        await Promise.all([
          ...seasonOps,
          apiPut<ApiStatusResponse>(`/v1/shows/${id}/status`, { status: 'completed' }, typeQuery(type)),
        ]);
      },
      onMutate: async () => {
        await Promise.all([
          qc.cancelQueries({ queryKey: showKey(id, type) }),
          qc.cancelQueries({ queryKey: seasonsKey(id) }),
        ]);
        const previous = snapshotShowScope(qc, id, type);
        patchShow(
          qc,
          id,
          (title) => withProgress({ ...title, userStatus: 'completed' }, title.totalEpisodes ?? 0),
          type,
        );
        patchSeasonSummaries(qc, id, (s) => ({ ...s, watchedEpisodes: s.episodeCount }));
        patchAllSeasonDetails(qc, id, (e) => ({ ...e, watched: true }));
        return { previous };
      },
      onError: (_err, _vars, ctx) => {
        restoreShowScope(qc, id, ctx?.previous, type);
      },
      onSuccess: () => void promptRating(),
      onSettled: () => invalidateShowScope(qc, id),
    },
    () => translate('show.error.markFinished'),
  );
}

/**
 * `POST /v1/shows/:id/rewatch` + the companion status flip back to watching
 * (the endpoint never touches status — docs/api-schema.md). Series rewatches
 * reset episode progress for the new pass, same as the app.
 */
export function useRewatch(id: string, type?: TitleType | null) {
  const qc = useQueryClient();
  return useToastedMutation(
    {
      mutationFn: async () => {
        const current = qc.getQueryData<Title | null>(showKey(id, type));
        const body: Record<string, unknown> = { mutation_id: crypto.randomUUID() };
        if (current?.type === 'show') body.reset_episodes = true;
        await Promise.all([
          apiPost<ApiRewatchResponse>(`/v1/shows/${id}/rewatch`, body, typeQuery(type)),
          apiPut<ApiStatusResponse>(`/v1/shows/${id}/status`, { status: 'watching' }, typeQuery(type)),
        ]);
      },
      onMutate: async () => {
        await Promise.all([
          qc.cancelQueries({ queryKey: showKey(id, type) }),
          qc.cancelQueries({ queryKey: seasonsKey(id) }),
        ]);
        const previous = snapshotShowScope(qc, id, type);
        patchShow(
          qc,
          id,
          (title) =>
            withProgress(
              { ...title, userStatus: 'watching', rewatchCount: (title.rewatchCount ?? 0) + 1 },
              0,
            ),
          type,
        );
        patchSeasonSummaries(qc, id, (s) => ({ ...s, watchedEpisodes: 0 }));
        patchAllSeasonDetails(qc, id, (e) => ({ ...e, watched: false }));
        return { previous };
      },
      onError: (_err, _vars, ctx) => {
        restoreShowScope(qc, id, ctx?.previous, type);
      },
      onSettled: () => invalidateShowScope(qc, id),
    },
    () => translate('show.error.startRewatch'),
  );
}

/** `PUT /v1/shows/:id/rating` — the title's stars (hero, rating prompt, Library). */
export function useTitleRating(id: string, type?: TitleType | null) {
  const qc = useQueryClient();
  return useToastedMutation(
    {
      mutationFn: (rating: number | null) =>
        apiPut<ApiTitleRatingResponse>(`/v1/shows/${id}/rating`, { rating }, typeQuery(type)),
      onMutate: async (rating) => {
        await qc.cancelQueries({ queryKey: showKey(id, type) });
        const previous = qc.getQueryData<Title | null>(showKey(id, type));
        patchShow(qc, id, (title) => ({ ...title, userRating: rating }), type);
        return { previous };
      },
      onError: (_err, _rating, ctx) => {
        if (ctx?.previous !== undefined) qc.setQueryData(showKey(id, type), ctx.previous);
      },
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: showKey(id) });
        void qc.invalidateQueries({ queryKey: ['home'] });
      },
    },
    () => translate('show.error.rate'),
  );
}

export interface EpisodeRateVars {
  season: number;
  episode: number;
  rating: number | null;
}

/** `PUT /v1/shows/:id/seasons/:n/episodes/:e/rating` — episode stars. */
export function useEpisodeRating(id: string) {
  const qc = useQueryClient();
  return useToastedMutation(
    {
      mutationFn: (vars: EpisodeRateVars) =>
        apiPut<ApiRatingResponse>(
          `/v1/shows/${id}/seasons/${vars.season}/episodes/${vars.episode}/rating`,
          { rating: vars.rating },
        ),
      onMutate: async (vars) => {
        await qc.cancelQueries({ queryKey: seasonKey(id, vars.season) });
        const previous = qc.getQueryData<Season | null>(seasonKey(id, vars.season));
        patchSeasonDetail(qc, id, vars.season, (e) =>
          e.episodeNumber === vars.episode ? { ...e, userRating: vars.rating } : e,
        );
        return { previous };
      },
      onError: (_err, vars, ctx) => {
        if (ctx?.previous !== undefined) qc.setQueryData(seasonKey(id, vars.season), ctx.previous);
      },
      onSettled: (_data, _err, vars) => {
        void qc.invalidateQueries({ queryKey: seasonKey(id, vars.season) });
      },
    },
    () => translate('show.error.rate'),
  );
}

/** The status transition an episode/season mark causes, if any. */
export function statusAfterMark(
  title: Title,
  nextWatchedCount: number,
  markingWatched: boolean,
): UserShowStatus | null {
  const current = title.userStatus ?? null;
  const next = nextStatusOnEpisodeToggle(
    { status: current, watchedCount: title.watchedEpisodes ?? 0, totalEpisodes: title.totalEpisodes ?? 0 },
    nextWatchedCount,
    markingWatched,
  );
  return next === current ? null : next;
}

/** The next unwatched episode, from season summaries' watched counts. */
export interface NextEpisode {
  season: number;
  episode: number;
}

/**
 * Where the series resumes, from season summaries alone: after the furthest
 * watched episode (`@shared/resume` — skipped early seasons never pull it
 * back to S1·E1), estimated as `watched + 1` inside the resume season. Null
 * when caught up / there are no seasons (a movie).
 */
export function nextEpisodeFromSeasons(seasons: Season[] | null | undefined): NextEpisode | null {
  return resumeFromSummaries(seasons ?? []);
}

/** Summed episode progress across season summaries. */
export function progressFromSeasons(seasons: Season[]): { watched: number; total: number } {
  return seasons.reduce(
    (acc, s) => ({
      watched: acc.watched + Math.min(s.episodeCount, s.watchedEpisodes ?? 0),
      total: acc.total + s.episodeCount,
    }),
    { watched: 0, total: 0 },
  );
}

/**
 * The next unwatched episode of a series, derived from its season summaries
 * (`fetchSeasons`, shared `seasonsKey` cache — so the Show page, Up next and
 * the Continue action all read and patch one entry). 5-minute stale time: the
 * optimistic episode toggle patches the summaries in place, so the label
 * advances immediately without a refetch.
 */
export function useNextEpisode(id: string, enabled = true) {
  return useQuery({
    queryKey: seasonsKey(id),
    queryFn: () => fetchSeasons(id),
    staleTime: CATALOG_STALE_MS,
    enabled,
    select: nextEpisodeFromSeasons,
  });
}

/**
 * Progress hint for surfaces that know the title from a list row (Up next)
 * rather than from the detail cache. Only used when `['show', id]` is not
 * cached; the Show page always has the detail, so its behaviour is unchanged.
 */
export interface ContinueHint {
  status?: UserShowStatus | null;
  watchedCount?: number;
  totalEpisodes?: number;
}

/**
 * The Continue CTA: resolve the next unwatched episode (season summaries →
 * the exact episode from the cached/fetched season detail) and mark it
 * watched through the normal optimistic toggle. Needs only the seasons — the
 * status transition reads the cached title when present (Show page), else the
 * caller's `hint` (Up next), else the summed season progress with status
 * `watching`.
 */
export function useContinue(id: string, hint?: ContinueHint) {
  const qc = useQueryClient();
  const toggle = useEpisodeToggle(id);
  const hintRef = useRef(hint);
  hintRef.current = hint;
  const markNext = useCallback(async () => {
    let seasons = qc.getQueryData<Season[]>(seasonsKey(id));
    if (!seasons) {
      try {
        seasons = await qc.fetchQuery({
          queryKey: seasonsKey(id),
          queryFn: () => fetchSeasons(id),
        });
      } catch {
        return;
      }
    }
    if (!seasons) return;
    // Resume after the furthest watched episode (`@shared/resume`): the
    // resume season's detail pins the exact episode (and may roll over to
    // the next season's first one).
    const resumeAt = resumeSeason(seasons);
    if (!resumeAt) return;

    let detail = qc.getQueryData<Season | null>(seasonKey(id, resumeAt.seasonNumber));
    if (!detail) {
      try {
        detail = await qc.fetchQuery({
          queryKey: seasonKey(id, resumeAt.seasonNumber),
          queryFn: () => fetchSeason(id, resumeAt.seasonNumber),
        });
      } catch {
        detail = null;
      }
    }
    const target = resumeEpisode(seasons, resumeAt.seasonNumber, detail?.episodes ?? []);
    if (!target) return;

    const cached = qc.getQueryData<Title | null>(showKey(id));
    const fromSeasons = progressFromSeasons(seasons);
    const progress = cached
      ? {
          status: cached.userStatus ?? null,
          watchedCount: cached.watchedEpisodes ?? 0,
          totalEpisodes: cached.totalEpisodes ?? 0,
        }
      : {
          status: hintRef.current?.status ?? 'watching',
          watchedCount: hintRef.current?.watchedCount ?? fromSeasons.watched,
          totalEpisodes: hintRef.current?.totalEpisodes || fromSeasons.total,
        };
    const current = progress.status;
    const next = nextStatusOnEpisodeToggle(
      { status: current, watchedCount: progress.watchedCount, totalEpisodes: progress.totalEpisodes },
      progress.watchedCount + 1,
      true,
    );

    toggle.mutate({
      season: target.season,
      episode: target.episode,
      watched: true,
      statusAfter: next === current ? null : next,
    });
  }, [qc, id, toggle]);
  return { markNext, isPending: toggle.isPending };
}
